if (!customElements.get('product-share')) {
  customElements.define('product-share', class ProductShare extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-share-trigger]') || this.querySelector('.product-share__trigger');
      this.overlay = this.querySelector('[data-share-overlay]');
      this.dialog = this.querySelector('.product-share__dialog');
      if (this.overlay && this.overlay.parentNode !== document.body) document.body.appendChild(this.overlay);
      this.closeButton = this.overlay.querySelector('[data-share-close]');
      this.copyButton = this.overlay.querySelector('[data-share-copy]');
      this.moreButton = this.overlay.querySelector('[data-share-more]');
      this.status = this.overlay.querySelector('[data-share-status]');
      this.boundKeydown = this.onKeydown.bind(this);
      this.trigger.addEventListener('click', () => this.open());
      this.closeButton.addEventListener('click', () => this.close());
      this.overlay.addEventListener('click', (event) => { if (event.target === this.overlay) this.close(); });
      this.copyButton.addEventListener('click', () => this.copy());
      this.moreButton.addEventListener('click', () => this.more());
      this.setLinks();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.boundKeydown);
      document.body.classList.remove('product-share-open');
      if (this.overlay && this.overlay.parentNode === document.body) this.overlay.remove();
    }

    setLinks() {
      const url = window.location.href;
      const encodedUrl = encodeURIComponent(url);
      const title = encodeURIComponent(document.title);
      const links = {
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
        pinterest: `https://pinterest.com/pin/create/button/?url=${encodedUrl}&description=${title}`,
        whatsapp: `https://wa.me/?text=${encodeURIComponent(document.title + ' ' + url)}`,
        messenger: `https://www.facebook.com/dialog/send?link=${encodedUrl}&app_id=966242223397117`,
        twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${title}`,
        email: `mailto:?subject=${title}&body=${encodedUrl}`
      };
      Object.entries(links).forEach(([service, href]) => {
        const link = this.overlay.querySelector(`[data-share-service="${service}"]`);
        if (link) link.href = href;
      });
      this.url = url;
    }

    open() {
      this.setLinks();
      this.overlay.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('product-share-open');
      document.addEventListener('keydown', this.boundKeydown);
      this.closeButton.focus();
    }

    close() {
      this.overlay.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('product-share-open');
      document.removeEventListener('keydown', this.boundKeydown);
      this.trigger.focus();
    }

    onKeydown(event) { if (event.key === 'Escape') this.close(); }

    copy() {
      const done = () => {
        this.copyButton.querySelector('[data-share-copy-label]').textContent = 'Copied';
        window.setTimeout(() => {
          this.copyButton.querySelector('[data-share-copy-label]').textContent = 'Copy Link';
        }, 2200);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(this.url).then(done);
      else {
        const input = document.createElement('input');
        input.value = this.url;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        input.remove();
        done();
      }
    }

    more() {
      if (navigator.share) navigator.share({ title: document.title, url: this.url }).catch(() => {});
      else this.copy();
    }
  });
}

if (!customElements.get('product-gallery-share')) {
  customElements.define('product-gallery-share', class ProductGalleryShare extends HTMLElement {
    connectedCallback() {
      this.copyButton = this.querySelector('[data-share-copy-inline]');
      if (!this.copyButton) return;
      this.boundCopy = this.copy.bind(this);
      this.copyButton.addEventListener('click', this.boundCopy);
      this.setupAlignment();
    }

    disconnectedCallback() {
      if (this.copyButton && this.boundCopy) this.copyButton.removeEventListener('click', this.boundCopy);
      this.alignmentObserver?.disconnect();
      window.removeEventListener('resize', this.boundAlign);
    }

    // The gallery's bottom margin decides where it stops when both sticky columns reach the end
    // of the product section; size it from the space under the purchase buttons so the share
    // row's bottom lines up with them however tall the content below the buttons is.
    setupAlignment() {
      this.alignmentGallery = this.closest('gallery-section[data-product-page]');
      this.alignmentMain = this.alignmentGallery?.parentElement?.querySelector(':scope > .wt-product__main');
      if (!this.alignmentMain) return;
      this.boundAlign = this.align.bind(this);
      this.alignmentObserver = new ResizeObserver(this.boundAlign);
      this.alignmentObserver.observe(this.alignmentMain);
      const button = this.alignmentMain.querySelector('button[name="add"]');
      if (button) this.alignmentObserver.observe(button);
      window.addEventListener('resize', this.boundAlign, { passive: true });
    }

    align() {
      const main = this.alignmentMain;
      const button = main.querySelector('button[name="add"]');
      if (!button || !button.offsetParent) return;
      // Sum offsets instead of client rects so reveal-animation transforms do not skew the result.
      let buttonBottom = button.offsetHeight;
      for (let element = button; element && element !== main; element = element.offsetParent) {
        buttonBottom += element.offsetTop;
        if (!main.contains(element.offsetParent)) return;
      }
      const space = main.offsetHeight + parseFloat(getComputedStyle(main).marginBottom) - buttonBottom;
      this.alignmentGallery.style.setProperty('--product-share-align-space', `${Math.max(space, 0)}px`);
    }

    async copy() {
      const url = this.copyButton.dataset.shareUrl || window.location.href;
      try {
        if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(url);
        else {
          const input = document.createElement('input');
          input.value = url;
          document.body.appendChild(input);
          input.select();
          document.execCommand('copy');
          input.remove();
        }
        this.copyButton.setAttribute('aria-label', 'Copied');
        window.setTimeout(() => this.copyButton.setAttribute('aria-label', 'Copy link'), 2200);
      } catch (error) {
        // Keep the share control usable when clipboard access is unavailable.
      }
    }
  });
}
